<template>
  <v-row>
    <v-col cols="12">
      <div class="upload-section">
        <v-progress-circular
          v-if="showPreloader"
          indeterminate
          color="primary"
          size="32"
          class="ml-4"></v-progress-circular>
        <input
          ref="pdfInput"
          :loading="showPreloader"
          type="file"
          accept=".tif, .tiff, .pdf, .png, .jpg, .jpeg"
          :disabled="props.disabled"
          :class="{ 'hidden-input': showPreloader }"
          @change="handleFileUpload" />
        <v-alert v-if="uploadError" type="error" variant="tonal" density="compact" class="mt-2">
          {{ uploadError }}
        </v-alert>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, watch } from "vue";
import { uuid } from "vue-uuid";
import { uploadService, needsServerConversion } from "@/services/uploadService";

const props = defineProps({
  downloadDestination: {
    type: String,
    default() {
      return "";
    },
  },
  downloadLabel: {
    type: String,
    default() {
      return "";
    },
  },
  downloadDestinationIndex: {
    type: Number,
    default() {
      return null;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emit = defineEmits(["downloadLink"]);
const showPreloader = ref(false);
let uploadKey = null;
const downloadLink = ref(null);
const fileName = ref(null);

const pdfInput = ref(null);
const allowedFileTypes = ["application/pdf", "image/png", "image/jpeg", "image/tiff"];

const uploadError = ref(null);

const handleFileUpload = async (event) => {
  showPreloader.value = true;
  uploadError.value = null;
  const file = event.target.files[0];
  if (!file) {
    showPreloader.value = false;
    return;
  }
  if (!allowedFileTypes.includes(file.type) && !needsServerConversion(file, file.name)) {
    uploadError.value = "Invalid file type. Please upload a PDF, PNG, JPG, or TIFF file.";
    showPreloader.value = false;
    return;
  }
  // allow only 20Mb
  if (file.size > 20971520) {
    uploadError.value = "File is too large. Maximum file size is 20MB.";
    showPreloader.value = false;
    return;
  }
  const uuidv4 = uuid.v4();
  uploadKey = uuidv4;
  fileName.value = file.name;
  uploadKey = uuidv4 + "/" + fileName.value;
  try {
    downloadLink.value = await uploadService.uploadFile(file, uploadKey);
  } catch (error) {
    // Extract error message from API response or error object
    const apiError = error?.error?.response?.data?.error;
    const directMessage = error?.message;
    uploadError.value = apiError || directMessage || "File upload failed. Please try again.";
    // Reset file input for retry
    if (pdfInput.value) pdfInput.value.value = "";
  } finally {
    showPreloader.value = false;
  }
};

watch(downloadLink, (newVal) => {
  if (newVal) {
    emit("downloadLink", {
      link: newVal,
      storeVar: props.downloadDestination,
      label: props.downloadLabel,
      storeVarIndex: props.downloadDestinationIndex,
      fileName: fileName.value,
      uploadKey: uploadKey,
    });
  }
});
</script>

<style lang="scss" scoped>
@import "../styles/components/_upload-file-component.scss";

.upload-loader {
  position: absolute;
  z-index: 1;
}

.hidden-input {
  opacity: 0;
  position: absolute;
  z-index: 0;
}
</style>
